import "./style.css"

import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

import Button from "@/components/Button/Button"
import LinkWithIcon from "@/components/LinkWithIcon/LinkWithIcon"
import { MODALS } from "@/constants/modals"
import { useSettingsData } from "@/queries/queries"
import type { Round } from "@/types/roundTypes"
import { allowAttendOnlineLecture } from "@/utils/helpers"
import { googleMeetIcon } from "@/utils/variables"
import { useStore } from "@/zustand/store"
import moment from "moment"

import { useGenerateCertificate } from "@/queries/mutations/certificateMutations"
import { toast } from "react-toastify"

const RoundCard = ({ ele }: { ele: Round }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { mutate: generateCertificate, isPending } = useGenerateCertificate()

  const handleGenerateCertificate = () => {
    generateCertificate(
      { roundId: ele.id },
      {
        onSuccess: () => {
          toast.success(
            t(
              "certificates.generatedSuccess",
              "Certificate generated successfully!"
            )
          )
          navigate("/certificates")
        },
      }
    )
  }

  const editModalState = useStore((store) => store.editModalState)

  const RoundTypes = [
    { value: 1, label: t("enrolledCoursesPage.rounds.types.online") },
    { value: 0, label: t("enrolledCoursesPage.rounds.types.offline") },
    { value: 2, label: t("enrolledCoursesPage.rounds.types.hybrid") },
  ]

  const getLabel = (id: number) => {
    const type = RoundTypes.find((item) => item.value === id)
    return type ? type.label : "--"
  }

  const { data: settings } = useSettingsData()
  const googleMeetSetting = settings?.find(
    (item) => item.key === "allow_google_meet_link"
  )

  const latestLecture = ele?.lectures?.[0]
  const showLatestLectureLink =
    latestLecture &&
    moment(latestLecture?.start_time).isBetween(
      moment().subtract(3, "hour"),
      moment().add(3, "hour")
    ) &&
    ele.type === 1

  const showGoogleMeetLink =
    Number(googleMeetSetting?.value) == 1 && ele?.google_meet_link

  const { attendance_rate = 0, task_submission_rate = 0 } = ele || {}

  const allowAttendLecture = allowAttendOnlineLecture(latestLecture, ele)

  return (
    <div>
      <div className="course-card">
        <div className="course-header">
          <span className="course-title">{ele?.name}</span>

          <div className="d-flex  flex-column gap-1 justify-content-center align-items-center">
            <span className="badge">
              {t("enrolledCoursesPage.rounds.status.active")}
            </span>
          </div>
        </div>
        <div className="d-flex mt-2 justify-content-between">
          <span className="tag">{ele?.course?.title}</span>
          <span className="tag-2">{getLabel(Number(ele?.type))}</span>
        </div>
        <div className="rates">
          <div className="rate-box">
            <h2
              className={
                attendance_rate < 50
                  ? "text-danger"
                  : attendance_rate < 80
                    ? "text-warning"
                    : "text-success"
              }
            >
              {attendance_rate}%
            </h2>
            <p>{t("enrolledCoursesPage.rounds.attendanceRate")}</p>
          </div>
          <div className="rate-box orange">
            <h2
              className={
                task_submission_rate < 50
                  ? "text-danger"
                  : task_submission_rate < 80
                    ? "text-warning"
                    : "text-success"
              }
            >
              {task_submission_rate}%
            </h2>
            <p>{t("enrolledCoursesPage.rounds.taskDegreeRate")}</p>
          </div>
        </div>

        {latestLecture && (showLatestLectureLink || showGoogleMeetLink) && (
          <div className="features">
            <div className="feature">
              <LinkWithIcon
                icon={googleMeetIcon}
                title={t("enrolledCoursesPage.rounds.meetingLink")}
                onClick={() => {
                  if (allowAttendLecture) {
                    editModalState(MODALS.lecture.attend, {
                      lecture: latestLecture,
                      round: ele,
                    })
                  }
                }}
              />
            </div>
          </div>
        )}

        {ele.status_stats === 3 && (
          <Button
            fixedWidth
            className="w-100 mt-3"
            variant="normal"
            onClick={handleGenerateCertificate}
            disabled={isPending}
          >
            {isPending
              ? t("buttons.loading")
              : t("buttons.generateCertificate", "Generate Certificate")}
          </Button>
        )}

        <Button
          fixedWidth
          className="w-100 mt-3"
          variant="lightBlue"
          onClick={() => navigate(`round/${ele?.id}/plan`)}
        >
          {t("enrolledCoursesPage.rounds.viewPlan")}
        </Button>
        <Link to={`round/${ele?.id}`} className="btn-custom">
          {t("enrolledCoursesPage.rounds.viewDetails")}
        </Link>
      </div>
    </div>
  )
}

export default RoundCard
